import { useEditor, EditorContent } from "@tiptap/react"
import StarterKit from "@tiptap/starter-kit"
import type { ParagraphBlockContent, RichTextContent } from "../types"
import { Card, CardContent, CardHeader } from "@/components/ui/card"
import { useCallback, useEffect } from "react"

interface ParagraphBlockEditorProps {
  content: ParagraphBlockContent | null
  onChange: (content: ParagraphBlockContent) => void
}

/** Default empty TipTap document */
const EMPTY_DOC = {
  type: "doc",
  content: [{ type: "paragraph" }],
}

function textToTipTapDoc(text: string): RichTextContent {
  return {
    type: "doc",
    content: [{ type: "paragraph", content: [{ type: "text", text }] }],
  }
}

export function ParagraphBlockEditor({ content, onChange }: ParagraphBlockEditorProps) {
  const initialContent = (
    content?.content ?? (content?.text ? textToTipTapDoc(content.text) : EMPTY_DOC)
  ) as RichTextContent

  const editor = useEditor({
    extensions: [StarterKit],
    content: initialContent,
    editorProps: {
      attributes: {
        class:
          // NOS-221: base `prose` to match ParagraphBlock's render wrapper (WYSIWYG parity)
          "min-h-[120px] ProseMirror outline-none focus:outline-none prose max-w-none",
      },
    },
  })

  const emitChange = useCallback(() => {
    if (!editor) return
    const json = editor.getJSON()
    const text = editor.getText()
    onChange({ ...content, content: json, text })
  }, [editor, onChange, content])

  useEffect(() => {
    if (!editor) return
    editor.on("update", emitChange)
    return () => {
      editor.off("update", emitChange)
    }
  }, [editor, emitChange])

  return (
    <Card>
      <CardHeader>
        <h3 className="text-base font-medium">Paragraph</h3>
        <p className="text-sm text-muted-foreground">
          Rich text with formatting.
        </p>
      </CardHeader>
      <CardContent>
        <div className="rounded-md border border-input bg-background px-3 py-2 focus-within:ring-2 focus-within:ring-ring">
          <EditorContent editor={editor} />
        </div>
      </CardContent>
    </Card>
  )
}
